import { ColumnProps } from "antd/lib/table";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Tag, Tooltip } from "antd";
import {
  ShopOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import TripContext from "../../../context/trip/context";
import {
  ITrip,
  TripStatus,
  PaymentStatus,
  getPaymentStatusColor,
  getPaymentStatusLabel,
} from "../../../models/trip/response";
import Space from "../../general/antd/space";
import ViewBtn from "../../general/table-components/actions/view-btn";
import EditBtn from "../../general/table-components/actions/edit-btn";
import { ISort, IStaticFilter } from "../../../models/base/base-query";
import { tableOnChange } from "../../../utils/helpers/table-sorts-filters";
import Table from "../../general/table-components/table";

interface IProps {}

const TripTable: React.FC<IProps> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { list, actions, loading, query } = useContext(TripContext);

  const getStatusTag = (status: TripStatus) => {
    switch (status) {
      case TripStatus.PENDING:
        return <Tag color="blue">{t("pending")}</Tag>;
      case TripStatus.ACTIVE:
        return <Tag color="green">{t("active")}</Tag>;
      case TripStatus.COMPLETED:
        return <Tag color="success">{t("completed")}</Tag>;
      case TripStatus.CANCELLED:
        return <Tag color="error">{t("cancelled")}</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const columns: ColumnProps<ITrip>[] = [
    {
      title: t("id"),
      dataIndex: "id",
      key: "id",
      sorter: {
        multiple: 1,
      },
      width: 80,
      align: "center",
    },
    {
      title: t("trip_number"),
      dataIndex: "number",
      key: "number",
      width: 150,
      align: "center",
      render: (value: string) => value || "-",
    },
    {
      title: t("driver"),
      dataIndex: ["driver", "firstName"],
      align: "center",
      key: "driver",
      width: 150,
      render: (_: any, record: ITrip) =>
        record.driver
          ? `${record.driver.firstName} ${record.driver.lastName}`
          : "-",
    },
    {
      title: t("driver_classification"),
      dataIndex: ["driver", "driverClassification", "name"],
      align: "center",
      key: "driverClassification",
      width: 150,
      render: (_: any, record: ITrip) =>
        record.driver?.driverClassification?.name || "-",
    },
    {
      title: t("store"),
      dataIndex: ["driver", "store", "name"],
      align: "center",
      key: "store",
      width: 150,
      render: (_: any, record: ITrip) => record.driver?.store?.name || "-",
    },
    {
      title: t("status"),
      dataIndex: "status",
      align: "center",
      key: "status",
      sorter: {
        multiple: 2,
      },
      width: 120,
      render: (status: TripStatus) => getStatusTag(status),
    },
    {
      title: t("orders_count"),
      dataIndex: "ordersCount",
      align: "center",
      key: "ordersCount",
      sorter: {
        multiple: 3,
      },
      width: 120,
      render: (_: number, record: ITrip) => {
        const completed = record.completedOrdersCount || 0;
        const total = record.ordersCount || 0;
        return (
          <Tooltip title={`${t("completed")}: ${completed} / ${total}`}>
            <span>
              <CheckCircleOutlined
                style={{ color: "#52c41a", marginInlineEnd: 4 }}
              />
              {completed}/{total}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: t("stores_count"),
      dataIndex: "storesCount",
      align: "center",
      key: "storesCount",
      width: 100,
      render: (count: number) => (
        <span>
          <ShopOutlined style={{ marginInlineEnd: 4 }} />
          {count || 0}
        </span>
      ),
    },
    {
      title: t("distance_for_payment"),
      dataIndex: "distanceForPayment",
      align: "center",
      key: "distanceForPayment",
      sorter: {
        multiple: 4,
      },
      width: 140,
      render: (_: number, record: ITrip) => {
        const distance = record.distanceForPayment ?? record.totalDistance;
        return distance != null ? (
          <Tooltip
            title={
              record.includesReturnTrip
                ? t("includes_return")
                : t("without_return")
            }
          >
            <span>
              {Number(distance).toFixed(2)} {t("km")}
            </span>
          </Tooltip>
        ) : (
          "-"
        );
      },
    },
    {
      title: t("base_cost"),
      dataIndex: "baseCost",
      align: "center",
      key: "baseCost",
      sorter: {
        multiple: 5,
      },
      width: 120,
      render: (value: number) =>
        value != null ? `${Number(value).toFixed(2)} ${t("currency")}` : "-",
    },
    {
      title: t("addition_cost"),
      dataIndex: "bonusCost",
      align: "center",
      key: "bonusCost",
      sorter: {
        multiple: 6,
      },
      width: 120,
      render: (value: number) =>
        value != null ? `${Number(value).toFixed(2)} ${t("currency")}` : "-",
    },
    {
      title: t("cost_for_payment"),
      dataIndex: "costForPayment",
      align: "center",
      key: "costForPayment",
      sorter: {
        multiple: 7,
      },
      width: 140,
      render: (_: number, record: ITrip) => {
        const cost = record.costForPayment ?? record.totalCost;
        return cost != null ? (
          <Tooltip
            title={
              record.includesReturnTrip
                ? t("includes_return")
                : t("without_return")
            }
          >
            <strong style={{ color: "#52c41a" }}>
              {Number(cost).toFixed(2)} {t("currency")}
            </strong>
          </Tooltip>
        ) : (
          "-"
        );
      },
    },
    {
      title: t("total_order_prices"),
      dataIndex: "totalOrderPrices",
      align: "center",
      key: "totalOrderPrices",
      sorter: {
        multiple: 8,
      },
      width: 130,
      render: (value: number) =>
        value != null ? `${Number(value).toFixed(2)} ${t("currency")}` : "-",
    },
    {
      title: t("duration_for_payment"),
      dataIndex: "durationForPayment",
      align: "center",
      key: "durationForPayment",
      sorter: {
        multiple: 9,
      },
      width: 130,
      render: (_: number, record: ITrip) => {
        const duration =
          record.durationForPayment ?? record.estimatedDurationMinutes;
        const actual = record.actualDurationMinutes;

        if (duration != null) {
          const tooltipContent =
            actual != null
              ? `${record.includesReturnTrip ? t("includes_return") : t("without_return")} | ${t("actual_duration")}: ${actual} ${t("minute_short")}`
              : record.includesReturnTrip
                ? t("includes_return")
                : t("without_return");

          return (
            <Tooltip title={tooltipContent}>
              <span>
                <ClockCircleOutlined
                  style={{
                    marginInlineEnd: 4,
                    color: actual != null ? "#52c41a" : "#888",
                  }}
                />
                {duration} {t("minute_short")}
              </span>
            </Tooltip>
          );
        }
        return "-";
      },
    },
    {
      title: t("payment_status"),
      dataIndex: "paymentStatus",
      align: "center",
      key: "paymentStatus",
      width: 130,
      render: (status: PaymentStatus) =>
        status ? (
          <Tag color={getPaymentStatusColor(status)}>
            {t(getPaymentStatusLabel(status))}
          </Tag>
        ) : (
          "-"
        ),
    },
    {
      title: t("started_at"),
      dataIndex: "startedAt",
      align: "center",
      key: "startedAt",
      sorter: {
        multiple: 10,
      },
      width: 150,
      render: (date: string) => (date ? new Date(date).toLocaleString() : "-"),
    },
    {
      title: t("completed_at"),
      dataIndex: "completedAt",
      align: "center",
      key: "completedAt",
      sorter: {
        multiple: 11,
      },
      width: 150,
      render: (date: string) => (date ? new Date(date).toLocaleString() : "-"),
    },
    {
      title: t("actions"),
      width: 150,
      dataIndex: "",
      align: "center",
      key: "actions",
      fixed: "right",
      render: (_: any, record: ITrip) => {
        const canEdit = record.status === TripStatus.PENDING;
        return (
          <Space>
            <ViewBtn
              onClick={(e) => {
                const url = `/trips/details/${record?.id}`;
                if (e.ctrlKey || e.metaKey) {
                  window.open(url, "_blank");
                } else {
                  navigate(url);
                }
              }}
            />
            {canEdit && (
              <EditBtn
                onClick={(e) => {
                  const url = `/trips/update/${record?.id}`;
                  if (e.ctrlKey || e.metaKey) {
                    window.open(url, "_blank");
                  } else {
                    navigate(url);
                  }
                }}
              />
            )}
          </Space>
        );
      },
    },
  ];

  // Filters && Sorts
  let tableFiltersProps = {};
  const [sorts, setSorts] = useState<ISort[]>([]);
  const [, setStaticFilters] = useState<IStaticFilter[]>(query?.filters ?? []);

  useEffect(() => {
    sorts.length > 0 &&
      actions.setQuery({
        ...query,
        orders: sorts,
      });
  }, [sorts]);

  return (
    <>
      <Table<any>
        rowKey="id"
        showPagination={true}
        columns={columns}
        dataSource={list?.data ?? []}
        loading={loading.includes("list") || list == undefined}
        size="small"
        total={list?.totalCount}
        pageSize={query.perPage}
        page={query.page}
        onPaginationChange={(page, pageSize) => {
          actions.setQuery({
            ...query,
            page,
            perPage: pageSize,
          });
        }}
        onChange={(_, tableFilters, tabelSorters) => {
          tableOnChange(
            tableFilters,
            tabelSorters,
            tableFiltersProps,
            setSorts,
            setStaticFilters,
          );
        }}
      />
    </>
  );
};

export default TripTable;
